import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { http } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Trash, PencilSimple, UserGear } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Field, inputCls } from "@/pages/PipelinePage";

export default function UsersPage() {
  const { t } = useI18n();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await http.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const del = async (u) => {
    if (u.id === me.id) return toast.error("Tidak bisa menghapus akun sendiri");
    if (!window.confirm(`Hapus ${u.name}?`)) return;
    await http.delete(`/users/${u.id}`);
    toast.success("Anggota dihapus");
    load();
  };

  const roleColor = { admin: "#1C4E63", manager: "#2F7D32", sales: "#0F2A3A" };

  return (
    <AppLayout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4 mb-5 sm:mb-8">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#64748B] font-bold">Administration</div>
          <h1 className="font-display text-2xl sm:text-4xl lg:text-5xl tracking-tight mt-1 text-[#173042]">Manajemen Tim</h1>
          <p className="text-[#64748B] mt-1 text-xs sm:text-sm">Tambah dan kelola akun sales, manager, dan admin.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowModal(true); }} data-testid="new-user-btn"
          className="self-start sm:self-auto bg-[#173B4D] hover:bg-[#112B38] text-white px-4 py-2 rounded-lg flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider shadow-xs transition-colors">
          <Plus size={16} weight="bold" /> <span>{t("action.newUser")}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4 stagger" data-testid="users-grid">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-card-${u.id}`} className="bg-white rounded-xl border border-[#E2E8F0] p-4 sm:p-5 shadow-2xs hover:shadow-card-hover transition-shadow flex flex-col justify-between">
            <div>
              <div className="flex items-start gap-3.5">
                {u.avatar_url ? (
                  <img src={u.avatar_url} alt={u.name} className="w-12 h-12 rounded-full object-cover border-2 border-[#173B4D]/20 shadow-2xs shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#173B4D] text-white flex items-center justify-center font-bold text-lg shadow-2xs shrink-0">{u.name?.[0] || "U"}</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-[#173042] truncate">{u.name}</div>
                  <div className="text-xs font-mono-data text-[#64748B] truncate">{u.email}</div>
                  <div className="mt-1.5 inline-block text-[9px] uppercase tracking-wider font-bold rounded px-1.5 py-0.2 border" style={{ color: roleColor[u.role], borderColor: roleColor[u.role] }}>
                    {t(`role.${u.role}`)}
                  </div>
                </div>
              </div>
              {u.phone && <div className="mt-3 text-xs font-mono-data text-[#64748B]">Telp: {u.phone}</div>}
            </div>
            <div className="mt-3.5 pt-3 border-t border-[#F1F5F9] flex justify-end gap-2">
              <button onClick={() => { setEditing(u); setShowModal(true); }} data-testid={`edit-user-${u.id}`} className="p-1.5 rounded text-[#64748B] hover:text-[#173042] hover:bg-[#F1F5F9]" title="Edit"><PencilSimple size={15} /></button>
              {u.id !== me.id && (
                <button onClick={() => del(u)} data-testid={`delete-user-${u.id}`} className="p-1.5 rounded text-[#64748B] hover:text-red-600 hover:bg-red-50" title="Hapus"><Trash size={15} /></button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <UserModal
          initial={editing}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}
    </AppLayout>
  );
}

function UserModal({ onClose, onSaved, initial }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    email: "", password: "", name: "", role: "sales", phone: "", avatar_url: "",
    ...(initial ? { ...initial, password: "" } : {}),
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(initial?.avatar_url || "");
  const [saving, setSaving] = useState(false);

  const onAvatarFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      return toast.error("File harus berupa gambar (JPEG, PNG, WebP, GIF)");
    }
    if (file.size > 3 * 1024 * 1024) {
      return toast.error("Ukuran file maksimal 3MB");
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const removeAvatar = async () => {
    setAvatarFile(null);
    setAvatarPreview("");
    setForm((f) => ({ ...f, avatar_url: "" }));
    if (initial?.id) {
      try {
        await http.delete(`/users/${initial.id}/avatar`);
        toast.success("Foto profil dihapus");
      } catch (err) {
        toast.error("Gagal menghapus foto profil");
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let savedUser = null;
      if (initial) {
        const payload = {};
        ["name", "role", "phone"].forEach((k) => { if (form[k] !== undefined) payload[k] = form[k]; });
        if (form.password) payload.password = form.password;
        const res = await http.patch(`/users/${initial.id}`, payload);
        savedUser = res.data;
        toast.success("User diperbarui");
      } else {
        const res = await http.post("/users", {
          email: form.email, password: form.password, name: form.name,
          role: form.role, phone: form.phone || undefined,
        });
        savedUser = res.data;
        toast.success("User dibuat");
      }

      // If a new avatar file was selected, upload it
      if (avatarFile && savedUser?.id) {
        const fd = new FormData();
        fd.append("file", avatarFile);
        await http.post(`/users/${savedUser.id}/avatar`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-3 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl border border-[#E2E8F0] w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden page-enter" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 sm:px-6 py-3.5 sm:py-4 border-b border-[#E2E8F0] flex items-center justify-between bg-[#F8FAFC] shrink-0">
          <div>
            <h3 className="font-bold text-sm sm:text-base text-[#173042] flex items-center gap-2">
              <UserGear size={20} className="text-[#173B4D]" />
              <span>{initial ? "Ubah Anggota" : "Anggota Baru"}</span>
            </h3>
            <p className="text-[11px] sm:text-xs text-[#64748B] mt-0.5">Kelola hak akses dan identitas tim CRM</p>
          </div>
          <button onClick={onClose} className="p-1 rounded text-[#64748B] hover:text-[#173042] hover:bg-[#E2E8F0]"><X size={18} weight="bold" /></button>
        </div>
        <form onSubmit={submit} className="flex flex-col flex-1 overflow-hidden" data-testid="user-form">
          <div className="p-4 sm:p-6 space-y-3.5 sm:space-y-4 overflow-y-auto flex-1">
            {/* Avatar Upload & Preview Section */}
            <div className="flex items-center gap-3.5 p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
              <div className="relative shrink-0">
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="Avatar Preview"
                    className="w-14 h-14 rounded-full object-cover border-2 border-[#173B4D] shadow-2xs"
                    data-testid="avatar-preview-img"
                  />
                ) : (
                  <div
                    className="w-14 h-14 rounded-full bg-[#173B4D] text-white flex items-center justify-center font-bold text-lg shadow-2xs"
                    data-testid="avatar-fallback"
                  >
                    {form.name?.[0] || "U"}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0 space-y-1">
                <div className="text-xs font-bold uppercase tracking-wider text-[#173042]">Foto Profil</div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="cursor-pointer inline-flex items-center px-3 py-1.5 rounded-lg bg-white border border-[#CBD5E1] text-xs font-semibold text-[#173042] hover:bg-slate-50 transition-colors shadow-2xs">
                    <span>Pilih Foto</span>
                    <input
                      type="file"
                      accept="image/png, image/jpeg, image/webp, image/gif"
                      onChange={onAvatarFileChange}
                      className="hidden"
                      data-testid="avatar-file-input"
                    />
                  </label>
                  {(avatarPreview || form.avatar_url) && (
                    <button
                      type="button"
                      onClick={removeAvatar}
                      data-testid="remove-avatar-btn"
                      className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition-colors"
                    >
                      Hapus
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-[#64748B]">Maks. 3MB (JPG, PNG, WebP, GIF)</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <Field label={t("common.name")} required>
                <input required data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Nama Lengkap" />
              </Field>
              <Field label={t("common.role")}>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} data-testid="user-role">
                  <option value="sales">Sales</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </Field>
            </div>
            <Field label={t("common.email")} required={!initial}>
              <input required={!initial} disabled={!!initial} type="email" data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls + (initial ? " opacity-50 cursor-not-allowed" : "")} placeholder="name@nusacert.co.id" />
            </Field>
            <Field label={initial ? "Kata Sandi Baru (opsional)" : "Kata Sandi"} required={!initial}>
              <input required={!initial} minLength={6} type="password" data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder={initial ? "Kosongkan bila tidak diubah" : "Minimal 6 karakter"} />
            </Field>
            <Field label={t("common.phone")}>
              <input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="08xx..." />
            </Field>
          </div>

          <div className="px-4 sm:px-6 py-3 border-t border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-end gap-2.5 shrink-0">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-[#CBD5E1] text-xs font-semibold uppercase tracking-wider text-[#64748B] hover:text-[#173042] hover:bg-[#F8FAFC]">{t("action.cancel")}</button>
            <button disabled={saving} type="submit" data-testid="user-submit" className="bg-[#173B4D] hover:bg-[#112B38] text-white px-5 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider disabled:opacity-60 transition-colors shadow-xs">
              {saving ? "..." : t("action.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
